import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ChevronRight, ChevronDown, Trash2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * Bannière "DLC Compliance" affichée en tête du Dashboard.
 *
 * Visible UNIQUEMENT si au moins 1 lot avec :
 *   - DLC dépassée (`dlc < today`)
 *   - encore en distributeur (`distributor_id != null`)
 *   - non sorti du stock (`quantity > 0`)
 *
 * → Indicateur d'oubli du process de retour DLC (compliance humaine).
 * Cliquer ouvre la liste détaillée + CTA "Voir le lot" pour la sortie de stock.
 *
 * Endpoint : GET /api/dlc-compliance/overdue-in-distributors
 */
export default function DlcComplianceBanner() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api
      .get("/dlc-compliance/overdue-in-distributors")
      .then((r) => setData(r.data))
      .catch(() => setData(null)); // silencieux : ne bloque pas le dashboard
  }, []);

  // Masquage par défaut : aucun lot périmé = aucune bannière (UI propre)
  if (!data || data.count === 0) return null;

  const { count, total_quantity, oldest_days, items } = data;
  const isUrgent = oldest_days >= 2; // 2j+ = vraiment urgent

  return (
    <div
      data-testid="dlc-compliance-banner"
      className={`border rounded-lg overflow-hidden shadow-sm ${
        isUrgent
          ? "bg-[#FBEEEA] border-[#C05C47]"
          : "bg-[#FBF4E9] border-[#C1A063]"
      }`}
    >
      <button
        onClick={() => setExpanded((v) => !v)}
        data-testid="dlc-compliance-banner-toggle"
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-black/[0.02] transition"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <AlertCircle
            className={`w-5 h-5 flex-shrink-0 ${
              isUrgent ? "text-[#C05C47]" : "text-[#8C6B2F]"
            }`}
          />
          <div className="min-w-0">
            <div
              className={`font-heading text-base font-medium ${
                isUrgent ? "text-[#A04E2C]" : "text-[#8C6B2F]"
              }`}
              data-testid="dlc-compliance-banner-title"
            >
              {count} lot{count > 1 ? "s" : ""} périmé{count > 1 ? "s" : ""} encore
              en distributeur{" "}
              {oldest_days >= 1 && (
                <span className="text-sm font-normal">
                  (jusqu&apos;à {oldest_days} jour{oldest_days > 1 ? "s" : ""} de retard)
                </span>
              )}
            </div>
            <div className="text-sm text-[#575A57] mt-0.5">
              {total_quantity} barquette{total_quantity > 1 ? "s" : ""} à récupérer
              et sortir du stock — process de retour DLC à appliquer
            </div>
          </div>
        </div>
        {expanded ? (
          <ChevronDown className="w-5 h-5 text-[#575A57] flex-shrink-0" />
        ) : (
          <ChevronRight className="w-5 h-5 text-[#575A57] flex-shrink-0" />
        )}
      </button>

      {expanded && (
        <div
          className="border-t border-[#E5E5E0] bg-white"
          data-testid="dlc-compliance-banner-list"
        >
          <div className="max-h-72 overflow-y-auto">
            {items.slice(0, 30).map((it) => (
              <div
                key={it.lot_id}
                className="flex items-center gap-3 px-5 py-2.5 border-b border-[#E5E5E0] last:border-b-0 text-sm hover:bg-[#F9F9F6]"
                data-testid={`dlc-compliance-item-${it.lot_id}`}
              >
                <span
                  className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${
                    it.days_overdue >= 2
                      ? "bg-[#C05C47] text-white"
                      : "bg-[#C1A063] text-white"
                  }`}
                >
                  J+{it.days_overdue}
                </span>
                <span className="font-mono text-xs text-[#575A57] w-28 flex-shrink-0 truncate">
                  {it.distributor_name}
                </span>
                <span className="flex-1 min-w-0 text-[#1A1C1A] truncate">
                  {it.product_name}
                  {it.emplacement_number !== null && it.emplacement_number !== undefined && (
                    <span className="text-[#849F90] text-xs ml-1.5">
                      · slot {it.emplacement_number}
                    </span>
                  )}
                </span>
                <span className="text-xs font-mono text-[#575A57] flex-shrink-0">
                  {it.quantity} unit. · DLC {it.dlc}
                </span>
                <span className="text-[10px] font-mono text-[#849F90] flex-shrink-0 truncate w-20">
                  {it.lot_number}
                </span>
              </div>
            ))}
            {items.length > 30 && (
              <div className="px-5 py-2 text-xs text-[#849F90] italic bg-[#F4F4F0]">
                + {items.length - 30} autre{items.length - 30 > 1 ? "s" : ""} lot{items.length - 30 > 1 ? "s" : ""} non affiché{items.length - 30 > 1 ? "s" : ""}…
              </div>
            )}
          </div>
          <div className="px-5 py-3 bg-[#F4F4F0] border-t border-[#E5E5E0] flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-[#575A57]">
              💡 Process : récupérer physiquement les barquettes → sortir du stock dans <strong>Stocks</strong> avec motif <em>PERIME_DLC</em>.
            </p>
            <Link
              to="/stocks?dlc=expired"
              className="inline-flex items-center gap-1.5 text-sm bg-[#C05C47] hover:bg-[#A04E2C] text-white px-3 py-1.5 rounded transition"
              data-testid="dlc-compliance-cta"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Traiter les sorties
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
